/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * UAW-2416 AC-3 / AC-4 — what the user can do about a sign-in error.
 *
 * Pure code → policy, no Redux and no React, modelled on
 * `model/videoplayer/error/ErrorDisplayRouting.ts` so the two read alike.
 * The tables are module-level constants rather than rebuilt per call: this
 * runs on SmartTV hardware, and the player's routing table made the same
 * choice for the same reason.
 *
 * One classification drives two consumers, deliberately:
 *
 *   - {@link resolveSignInErrorActions} — the buttons on the error alert.
 *   - {@link isTerminalSignInError} — whether the failure may enter CRITICAL
 *     retry at all. `RETRY_POLICY.CRITICAL` never gives up and never checks
 *     retryability (UAN-155 §2.3/§4.3), so a cause it cannot fix must be kept
 *     out of the retry wrapper entirely or the device retries forever behind
 *     an overlay with no escape.
 *
 * Keeping them together means the buttons and the retry decision cannot drift
 * apart for the *same* retry: an error offering "Try Again" is exactly one that
 * re-issuing the call can fix.
 *
 * {@link PROVISION_DEVICES_CODES} is the one place the two answers diverge, and
 * the divergence is the point. Every `v2/provisionDevices` failure offers
 * Retry / Exit, the pair every other sign-in error already offers, because the
 * alert's Retry is **not** the loop's retry: it dismisses back to the sign-in
 * form and restarts the journey from the top, which re-runs claim → bind →
 * domainToken → provision with a freshly minted token. Support cannot help with
 * that and Need Help left the user nothing to do, so the terminality answer is
 * unchanged — A462/A463/A464 stay out of CRITICAL backoff — while the buttons
 * are the recoverable pair. The exception is A463 ({@link DEVICE_LIMIT_CODES}):
 * a full device list does not change when the walk restarts, so it offers Need
 * Help / Exit instead.
 *
 * Groupings come from each code's `trigger` / `action` fields in the catalog
 * (docs/unity-client-error-codes).
 *
 * Every member is an `ErrorTypes` constant rather than a `"A46x"` literal.
 * These are the same numbers `DeviceProvisionServiceError` /
 * `DomainTokenServiceError` assign, so writing them out here would have been a
 * second home for a value that already has one — and the one place the drift
 * guard (`model/error/__tests__/errorCodeCatalogDrift.test.ts`) cannot see.
 * Referencing the table means a code renamed or retired upstream fails to
 * compile here instead of silently falling through to the default pair.
 */

import { ErrorTypes } from "../../../model/error";
import { CONNECTION_EXIT_ACTION_ID } from "../../connectionRetry";

/**
 * Actions an error alert can offer. Values are the alert's `id` field.
 *
 * Retry and Exit mirror the "Connection Error" overlay so the two blocking
 * surfaces read the same way. Exit carries the overlay's own id
 * ({@link CONNECTION_EXIT_ACTION_ID}) because its behaviour is identical —
 * `ConnectionRetryOverlayBridge` already tears down background retries and
 * closes the app when that id is dismissed, and is mounted app-wide.
 *
 * Retry deliberately does **not** reuse `CONNECTION_RETRY_ACTION_ID`. That id
 * wakes a running CRITICAL loop and keeps the overlay open with a pending
 * spinner; by the time this alert is up the loop has already given up, so
 * reusing it would hang on a spinner nothing can clear. Here Retry dismisses
 * and returns to the sign-in form, which is where every one of these journeys
 * restarts — with the entered credentials still in the form, so a mistyped
 * password can be corrected and resubmitted.
 */
export const SignInErrorAction = {
  /**
   * Acknowledge and return to the sign-in form. The same landing place as
   * {@link SignInErrorAction.Retry}, offered where re-issuing the call is not
   * the point — the user has to change what they typed first.
   */
  Ok: "sign_in_ok",
  /** Dismiss and return to the sign-in form to run the flow again. */
  Retry: "sign_in_retry",
  /** Return to the credential form — the entered identity was rejected. */
  SignInAgain: "sign_in_again",
  /** Show the support phone number and device details. */
  NeedHelp: "need_help",
  /** Close the app. Rendered on TV and VegaOS only. */
  Exit: CONNECTION_EXIT_ACTION_ID
} as const;

export type SignInErrorActionId =
  (typeof SignInErrorAction)[keyof typeof SignInErrorAction];

/**
 * Transport and availability failures: the request never got a usable answer.
 * Retrying is the correct first response, and CRITICAL backoff may run.
 */
const TRANSIENT_CODES: ReadonlyArray<string> = [
  ErrorTypes.IDP_JWT_TOKEN_TIMEOUT_A406,
  ErrorTypes.IDP_JWT_TOKEN_CONNECTION_REFUSED_A407,
  ErrorTypes.IDP_JWT_TOKEN_CONNECTION_FAILED_A408,
  ErrorTypes.DOMAIN_TOKEN_ENDPOINT_LOCATION_A008,
  ErrorTypes.DOMAIN_TOKEN_INTERNAL_SERVER_ERROR_A432,
  ErrorTypes.DOMAIN_TOKEN_WATCHDOG_A434,
  ErrorTypes.DOMAIN_TOKEN_UNKNOWN_SERVER_A435,
  ErrorTypes.DOMAIN_TOKEN_CONNECTION_REFUSED_A436,
  ErrorTypes.DOMAIN_TOKEN_CONNECTION_FAILED_A437,
  ErrorTypes.DEVICE_PROVISION_APIGW_ERROR_A461,
  ErrorTypes.DEVICE_PROVISION_INTERNAL_SERVER_ERROR_A465,
  ErrorTypes.DEVICE_PROVISION_INTEGRATION_TIMEOUT_A466,
  ErrorTypes.DEVICE_PROVISION_UNKNOWN_SERVER_A469,
  ErrorTypes.DEVICE_PROVISION_CONNECTION_FAILED_A471
];

/**
 * The service answered, and the answer was "no". Retrying re-sends the same
 * rejected identity, so these must never enter CRITICAL retry — the user has
 * to supply something different.
 */
const AUTH_REJECTED_CODES: ReadonlyArray<string> = [
  // Step 3 is where a mistyped password lands, and these three are the reason
  // the 401/403 status check in `createCriticalRetryOptions` had to exist: the
  // endpoint had no catalog family, so a rejected credential resolved to `A010`
  // and retried forever behind the connection overlay. Now it is terminal by
  // code as well as by status.
  ErrorTypes.IDP_JWT_TOKEN_UNAUTHORIZED_A401,
  ErrorTypes.IDP_JWT_TOKEN_TOKEN_REVOKED_A402,
  ErrorTypes.IDP_JWT_TOKEN_USER_NOT_FOUND_A404,
  // `IAS.INTERNAL_ERROR`, and the reason this list is not simply "the 4xx
  // ones": the identity service answers a wrong username or password with a
  // 500, not a 401. Classified by what it means at this endpoint rather than
  // by the status, because as a transient code it entered CRITICAL backoff —
  // which never gives up — and a mistyped password retried behind the
  // connection overlay until the app was killed.
  ErrorTypes.IDP_JWT_TOKEN_INTERNAL_SERVER_ERROR_A405,
  // `/claim` 401: the device key or the claim JWT signed with it was rejected.
  // Re-issuing the call re-sends the same JWT.
  ErrorTypes.CLAIM_UNAUTHORIZED_A901,
  // `/bind` 401: the IdP / IP JWT was rejected. Reported as A010 (the number
  // collides in the catalog — see authErrors.COLLIDING_CODES), so this entry
  // only ever matches through the raw-code check in `createCriticalRetryOptions`.
  ErrorTypes.BIND_UNAUTHORIZED_A701,
  ErrorTypes.DOMAIN_TOKEN_UNAUTHORIZED_A431,
  ErrorTypes.DEVICE_PROVISION_NOT_AUTHORIZED_A462
];

/**
 * The service rejected the request itself — a 400 — or, for auth config, has
 * nothing configured for this device. Re-issuing the identical request cannot
 * succeed, so these never enter CRITICAL backoff; before this list they did,
 * and looped behind the connection overlay.
 *
 * Buttons are the rejected-identity pair: the journey has to start again from
 * the form, and a repeat is a defect worth reporting.
 */
const CLIENT_REQUEST_REJECTED_CODES: ReadonlyArray<string> = [
  ErrorTypes.AUTH_CONFIG_BAD_REQUEST_A100,
  ErrorTypes.AUTH_CONFIG_RESOURCE_NOT_FOUND_A101,
  ErrorTypes.CLAIM_INVALID_REQUEST_A900,
  ErrorTypes.IDP_JWT_TOKEN_INVALID_REQUEST_A400,
  // Masked to A010 like A701 — matched through the raw code only.
  ErrorTypes.BIND_INVALID_REQUEST_A700,
  ErrorTypes.DOMAIN_TOKEN_INVALID_REQUEST_A430
];

/**
 * `v2/provisionDevices` failures that re-issuing the call cannot fix, beyond
 * the business rules below: a malformed request, and the two backend codes the
 * mapper has no catalog number for yet (they land on the A477 catch-all).
 *
 * The backend codes are matched on the raw service code
 * ({@link RESTART_REQUIRED_SERVICE_CODES}), because A477 alone also covers
 * failures that *are* worth retrying.
 */
const PROVISION_REQUEST_REJECTED_CODES: ReadonlyArray<string> = [
  ErrorTypes.DEVICE_PROVISION_BAD_REQUEST_A460
];

/**
 * Backend codes, as sent on the wire, that end the sign-in attempt outright:
 * recovery means re-running the journey from the start, which CRITICAL retry
 * of the one failed call never does.
 *
 * - `AUTHS.ACCOUNT_DATA_NOT_FOUND` (DPS 409) — the domain token carried no
 *   partnerCustomerId. Get a fresh account credential, bind, mint a new domain
 *   token, provision again.
 * - `AUTHS.DLID_PERMANENTLY_REMOVED` (DPS 410) — the device logging id was
 *   reset (Right-To-Be-Forgotten); the device must be set up again.
 *
 * Neither has a catalog number yet, so both surface as the A477 catch-all;
 * this list is what keeps them out of CRITICAL backoff meanwhile.
 */
export const RESTART_REQUIRED_SERVICE_CODES: ReadonlyArray<string> = [
  "AUTHS.ACCOUNT_DATA_NOT_FOUND",
  "AUTHS.DLID_PERMANENTLY_REMOVED"
];

/**
 * Codes whose alert is purely informational: the user reads the message,
 * acknowledges it, and is back on the form with what they typed still in the
 * fields. No second action is offered because there is no second thing to do —
 * re-sending the same credentials cannot succeed, and support cannot help with
 * a typo.
 *
 * Consulted ahead of the cause table, the same way {@link
 * PROVISION_DEVICES_CODES} is: the cause lists stay honest about *why* a code
 * fires and keep driving terminality, while this one decides the buttons.
 */
const ACKNOWLEDGE_ONLY_CODES: ReadonlyArray<string> = [
  ErrorTypes.IDP_JWT_TOKEN_INTERNAL_SERVER_ERROR_A405
];

/**
 * The account already holds its maximum number of devices (A463,
 * `AUTHS.TOO_MANY_DEVICES`).
 *
 * The one `v2/provisionDevices` code that {@link PROVISION_DEVICES_CODES} does
 * not decide the buttons for. Restarting the walk re-asks the same account the
 * same question, so Retry cannot help; the catalog's action is to deactivate a
 * device through the operator or its customer service, which is exactly what
 * Need Help shows. Checked ahead of the endpoint table.
 */
const DEVICE_LIMIT_CODES: ReadonlyArray<string> = [
  ErrorTypes.DEVICE_PROVISION_TOO_MANY_DEVICES_A463
];

/**
 * A business rule this device cannot satisfy. No amount of *re-issuing the same
 * call* changes the answer, so these never enter CRITICAL backoff.
 *
 * Both members are `v2/provisionDevices` codes, so
 * {@link PROVISION_DEVICES_CODES} claims them before the action table is
 * consulted and this list currently governs terminality alone. The branch in
 * {@link resolveSignInErrorActions} stays for the non-provision business-rule
 * families still to be allocated upstream.
 */
const BUSINESS_RULE_CODES: ReadonlyArray<string> = [
  ErrorTypes.DEVICE_PROVISION_TOO_MANY_DEVICES_A463,
  ErrorTypes.DEVICE_PROVISION_RESOURCE_NOT_FOUND_A464
];

/**
 * The call "succeeded" but the payload was unusable — empty, short or
 * undemarshallable. A retry sometimes clears a transient bad response, so it
 * is offered, but a repeat means a service defect worth reporting.
 */
const MALFORMED_RESPONSE_CODES: ReadonlyArray<string> = [
  ErrorTypes.IDP_JWT_TOKEN_TOKEN_NOT_FOUND_A403,
  ErrorTypes.IDP_JWT_TOKEN_CLIENT_BAD_REQUEST_A409,
  ErrorTypes.IDP_JWT_TOKEN_CLIENT_BAD_RESPONSE_A410,
  ErrorTypes.IDP_JWT_TOKEN_EMPTY_TOKEN_A411,
  ErrorTypes.IDP_JWT_TOKEN_UNKNOWN_ERROR_A412,
  ErrorTypes.DOMAIN_TOKEN_EMPTY_TOKEN_A433,
  ErrorTypes.DOMAIN_TOKEN_CLIENT_BAD_REQUEST_A438,
  ErrorTypes.DOMAIN_TOKEN_CLIENT_BAD_RESPONSE_A439,
  ErrorTypes.DOMAIN_TOKEN_SHORT_RESPONSE_A440,
  ErrorTypes.DOMAIN_TOKEN_DEMARSHAL_ERROR_A441,
  ErrorTypes.DEVICE_PROVISION_BAD_REQUEST_A460,
  ErrorTypes.DEVICE_PROVISION_NO_PAYLOAD_A467,
  ErrorTypes.DEVICE_PROVISION_EMPTY_BODY_ID_A468,
  ErrorTypes.DEVICE_PROVISION_EMPTY_BODY_ID_RESPONSE_A470,
  ErrorTypes.DEVICE_PROVISION_CLIENT_BAD_REQUEST_A472,
  ErrorTypes.DEVICE_PROVISION_CLIENT_BAD_RESPONSE_A473,
  ErrorTypes.DEVICE_PROVISION_SHORT_RESPONSE_A474,
  ErrorTypes.DEVICE_PROVISION_DEMARSHAL_ERROR_A475,
  ErrorTypes.DEVICE_PROVISION_DEMARSHAL_FAILED_A476
];

/**
 * Every `POST v2/provisionDevices` code — the whole `A460`-`A477` family that
 * `DeviceProvisionServiceError` can raise, including the `A477` catch-all its
 * `default` branch assigns.
 *
 * Listed by endpoint rather than by cause because the recovery is the same
 * whatever the cause: provisioning is the last step of the sign-in walk, and the
 * only thing the user can do about a failure there is run the walk again. That
 * makes Retry / Exit right for all of them and Need Help right for none — a
 * support phone number is not an action a user stuck at step 6 can take.
 *
 * Kept as its own list, ahead of the cause table, so the cause groupings above
 * stay honest about *why* each code fires (they still drive terminality) while
 * the buttons stay uniform across the endpoint.
 */
const PROVISION_DEVICES_CODES: ReadonlyArray<string> = [
  ErrorTypes.DEVICE_PROVISION_BAD_REQUEST_A460,
  ErrorTypes.DEVICE_PROVISION_APIGW_ERROR_A461,
  ErrorTypes.DEVICE_PROVISION_NOT_AUTHORIZED_A462,
  ErrorTypes.DEVICE_PROVISION_TOO_MANY_DEVICES_A463,
  ErrorTypes.DEVICE_PROVISION_RESOURCE_NOT_FOUND_A464,
  ErrorTypes.DEVICE_PROVISION_INTERNAL_SERVER_ERROR_A465,
  ErrorTypes.DEVICE_PROVISION_INTEGRATION_TIMEOUT_A466,
  ErrorTypes.DEVICE_PROVISION_NO_PAYLOAD_A467,
  ErrorTypes.DEVICE_PROVISION_EMPTY_BODY_ID_A468,
  ErrorTypes.DEVICE_PROVISION_UNKNOWN_SERVER_A469,
  ErrorTypes.DEVICE_PROVISION_EMPTY_BODY_ID_RESPONSE_A470,
  ErrorTypes.DEVICE_PROVISION_CONNECTION_FAILED_A471,
  ErrorTypes.DEVICE_PROVISION_CLIENT_BAD_REQUEST_A472,
  ErrorTypes.DEVICE_PROVISION_CLIENT_BAD_RESPONSE_A473,
  ErrorTypes.DEVICE_PROVISION_SHORT_RESPONSE_A474,
  ErrorTypes.DEVICE_PROVISION_DEMARSHAL_ERROR_A475,
  ErrorTypes.DEVICE_PROVISION_DEMARSHAL_FAILED_A476,
  // The class's own `default` branch — every provisioning failure it cannot map.
  ErrorTypes.DEVICE_PROVISION_UNKNOWN_ERROR_A477
];

function includesCode(codes: ReadonlyArray<string>, code: string): boolean {
  return codes.indexOf(code) !== -1;
}

/**
 * Whether retrying this failure can ever succeed.
 *
 * `true` means the cause is settled — the service gave a definitive answer, or
 * the device is not permitted — so the caller must route straight to the error
 * surface instead of handing the call to `RETRY_POLICY.CRITICAL`.
 *
 * Unknown codes are treated as **retryable**, matching the player router's
 * "uncatalogued is not an error condition" default. A wrongly-retried failure
 * is recoverable; a wrongly-terminated sign-in is not.
 */
export function isTerminalSignInError(code: string): boolean {
  return (
    includesCode(AUTH_REJECTED_CODES, code) ||
    includesCode(CLIENT_REQUEST_REJECTED_CODES, code) ||
    includesCode(BUSINESS_RULE_CODES, code) ||
    includesCode(PROVISION_REQUEST_REJECTED_CODES, code)
  );
}

/**
 * Whether the backend's own code (`getServiceErrorCode`) ends the attempt
 * regardless of the catalog code it was mapped to. See
 * {@link RESTART_REQUIRED_SERVICE_CODES}.
 */
export function isTerminalServiceErrorCode(
  serviceCode: string | undefined
): boolean {
  return (
    serviceCode !== undefined &&
    includesCode(RESTART_REQUIRED_SERVICE_CODES, serviceCode)
  );
}

/**
 * The actions to offer for `code`, most useful first.
 *
 * Total by construction: an unrecognised code falls through to the generic
 * pair, so a producer can raise a code before it is classified and still get a
 * usable alert. Never returns an empty array — an alert with no buttons is the
 * trap this work exists to remove.
 *
 * Exit is listed as policy, not as a promise that it renders: it is dropped on
 * anything that is not a TV or VegaOS, where the app cannot close itself. That
 * gate lives in `signInErrorAlert`, which is why Retry leads every list that
 * carries Exit — it is the action that always survives the filter.
 */
export function resolveSignInErrorActions(
  code: string
): ReadonlyArray<SignInErrorActionId> {
  // Ahead of the cause table on purpose: a provisioning failure is recovered by
  // restarting the sign-in walk, never by calling support, whatever the cause.
  if (includesCode(ACKNOWLEDGE_ONLY_CODES, code)) {
    return [SignInErrorAction.Ok];
  }
  if (includesCode(DEVICE_LIMIT_CODES, code)) {
    // Need Help survives the Exit filter, so the browser still has a button,
    // and its own alert's Close returns to the sign-in form.
    return [SignInErrorAction.NeedHelp, SignInErrorAction.Exit];
  }
  if (includesCode(PROVISION_DEVICES_CODES, code)) {
    return [SignInErrorAction.Retry, SignInErrorAction.Exit];
  }
  if (includesCode(BUSINESS_RULE_CODES, code)) {
    // Deliberately no Retry: the answer will not change, and offering it
    // invites the user to hammer a call that cannot succeed.
    return [SignInErrorAction.NeedHelp, SignInErrorAction.Exit];
  }
  if (
    includesCode(AUTH_REJECTED_CODES, code) ||
    includesCode(CLIENT_REQUEST_REJECTED_CODES, code)
  ) {
    return [SignInErrorAction.SignInAgain, SignInErrorAction.NeedHelp];
  }
  if (includesCode(MALFORMED_RESPONSE_CODES, code)) {
    return [SignInErrorAction.Retry, SignInErrorAction.NeedHelp];
  }
  if (includesCode(TRANSIENT_CODES, code)) {
    return [SignInErrorAction.Retry, SignInErrorAction.Exit];
  }
  return [SignInErrorAction.Retry, SignInErrorAction.Exit];
}

/**
 * Every code this module classifies **by cause** — used by the coverage test.
 *
 * A `v2/provisionDevices` code appears here for the cause it reports and in
 * {@link PROVISION_DEVICES_ERROR_CODES} for the buttons it gets; the two views
 * overlap deliberately, so nothing here says which actions a code resolves to.
 */
export const CLASSIFIED_SIGN_IN_ERROR_CODES: Readonly<{
  transient: ReadonlyArray<string>;
  authRejected: ReadonlyArray<string>;
  clientRejected: ReadonlyArray<string>;
  businessRule: ReadonlyArray<string>;
  malformed: ReadonlyArray<string>;
}> = {
  transient: TRANSIENT_CODES,
  authRejected: AUTH_REJECTED_CODES,
  clientRejected: CLIENT_REQUEST_REJECTED_CODES,
  businessRule: BUSINESS_RULE_CODES,
  malformed: MALFORMED_RESPONSE_CODES
};

/** The `v2/provisionDevices` family — used by the coverage test. */
export const PROVISION_DEVICES_ERROR_CODES: ReadonlyArray<string> =
  PROVISION_DEVICES_CODES;

/** The device-limit codes — used by the coverage test. */
export const DEVICE_LIMIT_ERROR_CODES: ReadonlyArray<string> =
  DEVICE_LIMIT_CODES;

/**
 * The codes whose buttons are decided by {@link ACKNOWLEDGE_ONLY_CODES} rather
 * than by their cause — used by the coverage test, which reads the cause-based
 * expectations on the codes the cause table still owns.
 */
export const ACKNOWLEDGE_ONLY_ERROR_CODES: ReadonlyArray<string> =
  ACKNOWLEDGE_ONLY_CODES;
